import { batch } from "solid-js";
import { reconcile } from "solid-js/store";
import { useSearchParams } from "@solidjs/router";
import FeatureStore from "@rock-solid/feature-store";

import CommunityPost, {
  CommunityPostUsa,
} from "@repo/kikoff-models/community/CommunityPost";
import { CommunityProfileUsa } from "@repo/kikoff-models/community/CommunityProfile";
import usRpc from "@repo/protobuf/api/usRpc";
import UArray from "@repo/utils/UArray";
import UObject from "@repo/utils/UObject";

import { useAdmin } from "./admin";
import { useProfiles } from "./profiles";

const initialState = {
  byId: {} as Record<CommunityPost.Id, CommunityPost>,
  failedById: {} as Record<CommunityPost.Id, string>,
  repliesById: {} as Record<CommunityPost.Id, CommunityPost.Id[]>,
  // Map of all replies, regardless of comment depth, should only be used on root posts
  repliesByRootId: {} as Record<CommunityPost.Id, CommunityPost.Id[]>,
  home: null as CommunityPost.Id[] | null,
  draft: {
    title: "",
    bodyMd: "",
    anonymous: false,
    showScore: false,
    tagIds: [] as CommunityPost.Tag.Id[],
  },
  userVoteById: {} as Record<CommunityPost.Id, CommunityPost.Vote>,
  userIsFollowingById: {} as Record<CommunityPost.Id, boolean>,
  page: 0,
  allTags: null as CommunityPost.Tag.Id[] | null,
  tagById: {} as Record<CommunityPost.Tag.Id, CommunityPost.Tag>,
};

export const { usePosts } = FeatureStore.init("Posts", initialState).setup(
  ([store, setStore], { attachAction, wrap }) => {
    const [, setProfiles] = useProfiles();
    const [admin, setAdmin] = useAdmin();
    const [searchParams] = useSearchParams();

    const accessors = wrap
      .accessors({
        byId: attachAction(
          (id: CommunityPost.Id) => store.byId[id],
          (id) => actions.fetch(id)
        ),
        failedById: (id: CommunityPost.Id) => store.failedById[id],
        home: Object.assign(
          attachAction(
            // eslint-disable-next-line no-unused-vars
            (sortMethod: CommunityPost.SortMethod) =>
              store.home?.map((id) => store.byId[id]!),
            (sortMethod) =>
              actions.fetchPage(1, sortMethod, searchParams.q as string)
          ),
          { ids: () => store.home }
        ),
        repliesFor: attachAction(
          (id: CommunityPost.Id) =>
            store.repliesById[id]?.map((id) => store.byId[id]!),
          (id) =>
            // Only root posts need to be fetched, they contain all nested replies
            !store.byId[id]?.parentId && actions.fetch(id)
        ),
        rootIdFor: function rootId(id: CommunityPost.Id) {
          const parentId = store.byId[id]?.parentId;
          if (!parentId) return id;
          return rootId(parentId);
        },
        queryRepliesTo: attachAction(
          (id: CommunityPost.Id, query: string) =>
            store.repliesByRootId[id]
              ?.map((id) => store.byId[id]!)
              .filter((post) => post.bodyMd?.toLowerCase().includes(query)),
          (id) =>
            // Only root posts need to be fetched to build a map of all it's replies (ignoring depth)
            !store.byId[id]?.parentId && actions.fetch(id)
        ),
        draft: () => store.draft,
        userVoteFor: (id: CommunityPost.Id) => store.userVoteById[id],
        userIsFollowingFor: (id: CommunityPost.Id) =>
          store.userIsFollowingById[id],
        page: () => store.page,
        allTags: attachAction(
          () => store.allTags?.map((id) => store.tagById[id]!),
          () => actions.fetchTags()
        ),
        tagById: attachAction(
          (id: CommunityPost.Tag.Id) => store.tagById[id],
          () => actions.fetchTags()
        ),
      })
      .finalize();

    const mutations = wrap
      .mutations({
        one(post: CommunityPost) {
          setStore("byId", post.id, reconcile(post));
        },
        many(posts: CommunityPost[]) {
          batch(() => {
            for (const post of posts) {
              mutations.one(post);
            }
          });
        },
      })
      .finalize();

    const actions = wrap
      .actions({
        fetchPage(
          page: number,
          sortMethod: CommunityPost.SortMethod,
          query?: string
        ) {
          return usRpc.Community.listCommunityPosts({
            page: page,
            sortMethod: CommunityPostUsa.SortMethod.denormalize(sortMethod),
            query: query,
          })
            .SUCCESS(
              ({ posts, userVoteByPostToken, userIsFollowingByPostToken }) => {
                return batch(() => {
                  const normalized = posts.map((post) =>
                    CommunityPostUsa.normalize(post)
                  );
                  mutations.many(normalized);
                  setProfiles.manySummaries(
                    UArray.dedupeBy(
                      posts.map(({ profile }) => profile!),
                      "token"
                    ).map(CommunityProfileUsa.Summary.normalize)
                  );
                  setStore("home", [
                    ...(page === 1 ? [] : (store.home ?? [])),
                    ...normalized.map((post) => post.id),
                  ]);
                  setStore(
                    "userVoteById",
                    UObject.transform(userVoteByPostToken, ([key, value]) => [
                      key,
                      CommunityPostUsa.Vote.normalize(value),
                    ])
                  );
                  setStore("userIsFollowingById", userIsFollowingByPostToken);
                  setStore("page", page);
                  return normalized;
                });
              }
            )
            .elseThrow("Failed to fetch posts");
        },
        fetch(id: CommunityPost.Id) {
          return usRpc.Community.getCommunityPost({ token: id })
            .SUCCESS(
              ({
                post,
                replies,
                userVoteByPostToken,
                userIsFollowingByPostToken,
              }) => {
                batch(() => {
                  const normalized = CommunityPostUsa.normalize(post!);
                  const normalizedReplies = replies.map((post) =>
                    CommunityPostUsa.normalize(post)
                  );
                  mutations.one(normalized);
                  mutations.many(normalizedReplies);

                  setStore(
                    "repliesById",
                    (() => {
                      const res: Record<CommunityPost.Id, CommunityPost.Id[]> =
                        {};
                      for (const reply of normalizedReplies) {
                        (res[reply.parentId as CommunityPost.Id] ??= []).push(
                          reply.id
                        );
                      }
                      return res;
                    })()
                  );

                  setStore(
                    "repliesByRootId",
                    normalized.id,
                    normalizedReplies.map(({ id }) => id)
                  );

                  setProfiles.oneSummary(
                    CommunityProfileUsa.Summary.normalize(post!.profile!)
                  );
                  setProfiles.manySummaries(
                    UArray.dedupeBy(
                      replies.map(({ profile }) => profile!),
                      "token"
                    ).map(CommunityProfileUsa.Summary.normalize)
                  );
                  setStore(
                    "userVoteById",
                    UObject.transform(userVoteByPostToken, ([key, value]) => [
                      key,
                      CommunityPostUsa.Vote.normalize(value),
                    ])
                  );
                  setStore("userIsFollowingById", userIsFollowingByPostToken);
                });
              }
            )
            .FAILED(({ error }) => {
              if (id)
                setStore("failedById", id, error!.description || error!.title);
            })
            .elseThrow("Failed to fetch post");
        },
        draft: Object.assign(
          (params: Partial<CommunityPost.Params>) => {
            setStore("draft", params);
          },
          {
            reset() {
              setStore("draft", structuredClone(initialState.draft));
            },
          }
        ),
        updateVotes(id: CommunityPost.Id, addedVotes: number) {
          setStore("byId", id, "upvoteCount", (votes) => votes + addedVotes);
        },
        create(params: CommunityPost.Params) {
          if (admin.profiles.current()) return setAdmin.post.create(params);

          return usRpc.Community.createCommunityPost(
            CommunityPostUsa.Params.denormalize(params)
          )
            .SUCCESS(({ post, userVote, userIsFollowing }) => {
              // TODO: While showScore is controlled by post creation, we'll need to manually refetch profile here.
              setProfiles.fetch();

              const normalized = CommunityPostUsa.normalize(post!);
              batch(() => {
                mutations.one(normalized);
                setProfiles.oneSummary(
                  CommunityProfileUsa.Summary.normalize(post!.profile!)
                );

                if (params.parentId) {
                  setStore("repliesById", params.parentId, (replies = []) => [
                    normalized.id,
                    ...replies,
                  ]);

                  setStore(
                    "repliesByRootId",
                    accessors.rootIdFor(normalized.id),
                    (replies = []) => [normalized.id, ...replies]
                  );
                } else if (store.home) {
                  setStore("home", (home) => {
                    const firstUnpinnedIndex = home!.findIndex(
                      (id) => !accessors.byId(id)!.isPinned
                    );
                    return [
                      ...home!.slice(0, firstUnpinnedIndex),
                      normalized.id,
                      ...home!.slice(firstUnpinnedIndex),
                    ];
                  });
                }

                setStore(
                  "userVoteById",
                  normalized.id,
                  CommunityPostUsa.Vote.normalize(userVote)
                );

                setStore("userIsFollowingById", normalized.id, userIsFollowing);
              });
              return normalized;
            })
            .elseThrow("Failed to create post");
        },
        vote(id: CommunityPost.Id, vote: CommunityPost.Vote) {
          return usRpc.Community.vote({
            postToken: id,
            voteType: CommunityPostUsa.Vote.denormalize(vote),
          })
            .SUCCESS(() => {
              batch(() => {
                const current = store.userVoteById[id];
                if (current)
                  setStore("byId", id, `${current}Count`, (count) => count - 1);
                if (vote)
                  setStore("byId", id, `${vote}Count`, (count) => count + 1);

                setStore("userVoteById", id, vote);
              });
            })
            .elseThrow(`Failed to ${vote || "unvote"} post.`);
        },
        follow(id: CommunityPost.Id) {
          return usRpc.Community.followCommunityPost({
            token: id,
          })
            .SUCCESS(() => {
              setStore("userIsFollowingById", id, true);
            })
            .elseThrow("Failed to follow post.");
        },
        unfollow(id: CommunityPost.Id) {
          return usRpc.Community.unfollowCommunityPost({
            token: id,
          })
            .SUCCESS(() => {
              setStore("userIsFollowingById", id, false);
            })
            .elseThrow("Failed to unfollow post.");
        },
        fetchTags() {
          return usRpc.Community.listCommunityTags({})
            .SUCCESS(({ tags }) => {
              const normalized = tags.map(CommunityPostUsa.Tag.normalize);
              setStore(
                "tagById",
                Object.fromEntries(normalized.map((tag) => [tag.id, tag]))
              );
              setStore(
                "allTags",
                normalized.map((tag) => tag.id)
              );
            })
            .elseThrow("Failed to fetch post tokens.");
        },
      })
      .finalize();

    return { accessors, actions };
  }
);
